import axios from "../axios";

/**
 * Foundation Data v2 API.
 *
 * No `/api` prefix — the axios instance's baseURL already includes it, and its request interceptor
 * attaches the bearer token, which is why none of these pass headers.
 *
 * Every function takes at most ONE params argument, because `callApi(method, params)` in AuthProvider
 * calls `method(params, token)` and silently drops anything after the first. Operations that need both an
 * id and a body therefore take a single object.
 */
const API_PATH = "/foundation-data-v2";

/*
  Most reads and writes are keyed on a DATA YEAR ID, not on a calendar year.

  A reporting year holds one row per workstream (GHG Emissions / Risks and Opportunities / Targets), so a
  year alone stopped naming one thing. The id comes from `listYears()`, which the detail page resolves from
  the question it was opened with. The one exception is `listCuratedRowsForAgent` on the server, which stays
  year-keyed because AI Review selects years rather than workstreams.
*/

const api = {
  /** `{ initialised, round }` — a wrapper, so a first-time org is not a 404. */
  getRound() {
    return axios.get(`${API_PATH}/round`).then(({ data }) => data);
  },

  initRound() {
    return axios.post(`${API_PATH}/init`).then(({ data }) => data);
  },

  setControllers(ownerEmails) {
    return axios.post(`${API_PATH}/set-controllers`, ownerEmails).then(({ data }) => data);
  },

  listYears() {
    return axios.get(`${API_PATH}/years`).then(({ data }) => data);
  },

  /**
   * Creates one row per workstream for the year, for every active entity, and returns all of them. Idempotent
   * per (entity, year, type).
   */
  createYear(year) {
    return axios.post(`${API_PATH}/years`, { year }).then(({ data }) => data);
  },

  /** Gives one entity the workstreams of a year the organisation already collects. Idempotent. */
  addEntityToYear({ year, entityId }) {
    return axios.post(`${API_PATH}/years/${year}/entities/${entityId}`).then(({ data }) => data);
  },

  listRows(dataYearId) {
    return axios.get(`${API_PATH}/data-years/${dataYearId}/rows`).then(({ data }) => data);
  },

  listRuns(dataYearId) {
    return axios.get(`${API_PATH}/data-years/${dataYearId}/runs`).then(({ data }) => data);
  },

  /**
   * The organisation's active target structure, or null when it has none.
   *
   * Null, not an empty object: the page renders differently for "we have not said what we want" than for
   * "we want nothing", and the server answers 204 for exactly that reason. Axios gives a 204 an empty-string
   * body, which would otherwise reach React Query as a falsy-but-not-null value.
   */
  getTargetStructure(questionGroup) {
    const query = questionGroup ? `?type=${encodeURIComponent(questionGroup)}` : "";
    return axios.get(`${API_PATH}/target-structure${query}`).then(({ data }) => data || null);
  },

  /**
   * Replaces the target structure with an uploaded workbook.
   *
   * Takes the FormData under `data` because that is the shape `ImportData` builds and passes — reusing that
   * component means matching its params contract rather than inventing a second one. No explicit
   * Content-Type: the browser has to set the multipart boundary itself, and naming the header without one
   * makes the request unparseable server-side.
   *
   * @param {{data: FormData, name?: string}} params
   */
  uploadTargetStructure({ data, name, questionGroup }) {
    const params = new URLSearchParams();
    if (name) params.set("name", name);
    if (questionGroup) params.set("type", questionGroup);
    const query = params.toString() ? `?${params}` : "";
    return axios.post(`${API_PATH}/target-structure${query}`, data).then(({ data: body }) => body);
  },

  /** The blank workbook to fill in. Returns the raw response so the caller can read the filename header. */
  downloadTargetStructureTemplate(questionGroup) {
    const query = questionGroup ? `?type=${encodeURIComponent(questionGroup)}` : "";
    return axios.get(`${API_PATH}/target-structure/template${query}`, { responseType: "blob" });
  },

  listCuratedRows(dataYearId) {
    return axios.get(`${API_PATH}/data-years/${dataYearId}/curated`).then(({ data }) => data);
  },

  /**
   * Every workstream's approved figures for one reporting year.
   *
   * For callers whose question is about the YEAR rather than one workstream — an answer's citation records a
   * year and a datapoint uid and has no idea which workstream a figure came from.
   */
  listCuratedRowsForYear(year) {
    return axios.get(`${API_PATH}/years/${year}/curated`).then(({ data }) => data);
  },

  /**
   * A usage badge per datapoint for one year: how many questions rest on each figure, and how many of those
   * were written against a version that has since changed.
   *
   * One request for the whole table rather than one per row — the server does the join in a single pass.
   */
  /**
   * What approving this workstream would publish (ESG-4012): every datapoint whose working version differs from
   * the approved one, as `{added, edited, removed}`. The approval hands their uids to the impact pre-flight.
   */
  listPendingChanges(dataYearId) {
    return axios.get(`${API_PATH}/data-years/${dataYearId}/curated/pending`).then(({ data }) => data);
  },

  listCuratedUsage(dataYearId) {
    return axios.get(`${API_PATH}/data-years/${dataYearId}/curated/usage`).then(({ data }) => data);
  },

  /** The questions answered from one datapoint. Keyed on the datapoint uid, not a row id: a row id names
   *  one VERSION, and this question is about the datapoint across all of them. */
  getDatapointUsage(datapointUid) {
    return axios.get(`${API_PATH}/datapoints/${datapointUid}/usage`).then(({ data }) => data);
  },

  /**
   * What changing a set of figures would mean for the requirements that used them.
   *
   * A POST because the uid list can be long, and because it is the shape a save's dry run hands back
   * (`supersededDatapointUids`). It reads and writes nothing — the pre-flight for "Manage changed data",
   * asked while the figure change can still be abandoned.
   *
   * Uids, not row ids: a row id names one VERSION and changes on every save, so asking by row id would miss
   * every answer built on an earlier version of the same datapoint.
   */
  getAnswerImpact(datapointUids) {
    return axios
      .post(`${API_PATH}/answer-impact`, { datapointUids: datapointUids ?? [] })
      .then(({ data }) => data);
  },

  /**
   * "Manage changed data" options 3 and 4: rewrite the affected answers' inline citations onto the newly
   * approved figures — a find-and-replace on the server, not an AI re-draft. `returnToProgress` (option 3) also
   * sends the submitted or approved ones that changed back to In Progress. Reports what actually happened.
   */
  updateAnswerFigures({ questionIds, datapointUids, returnToProgress }) {
    return axios
      .post(`${API_PATH}/answer-figure-update`, {
        questionIds: questionIds ?? [],
        datapointUids: datapointUids ?? [],
        returnToProgress: !!returnToProgress,
      })
      .then(({ data }) => data);
  },

  /** The foundation figures one question's answer rests on. `questionId` is the business id. */
  getQuestionFoundationUsage(questionId) {
    return axios
      .get(`${API_PATH}/questions/${questionId}/foundation-usage`)
      .then(({ data }) => data);
  },

  listCuratedSaves(dataYearId) {
    return axios.get(`${API_PATH}/data-years/${dataYearId}/curated/saves`).then(({ data }) => data);
  },

  /** @param {{dataYearId: number, row: object}} params */
  addCuratedRow({ dataYearId, row }) {
    return axios
      .post(`${API_PATH}/data-years/${dataYearId}/curated/rows`, row)
      .then(({ data }) => data);
  },

  /** @param {{id: number, data: object}} params */
  patchCuratedRow({ id, data }) {
    return axios.patch(`${API_PATH}/curated/rows/${id}`, data).then(({ data: body }) => body);
  },

  deleteCuratedRow(id) {
    return axios.delete(`${API_PATH}/curated/rows/${id}`).then(({ data }) => data);
  },

  /**
   * Promotes one extraction into the foundation data unchanged, or previews it with `dryRun`.
   *
   * `expectedReplacedRows` carries the count a preview reported, so a change landing between the preview
   * and the confirm is refused with a 409 rather than archiving a different number of rows than the user
   * agreed to. Same guard, same reason, as a chat save.
   *
   * `keepOffSchema` decides what happens to figures matching no target datapoint. Omitted rather than
   * defaulted here: the server treats absent as KEEP, and sending `true` from a caller that never asked the
   * user would make an unasked question look answered.
   *
   * @param {{runId: number, dryRun?: boolean, expectedReplacedRows?: number, keepOffSchema?: boolean}} params
   */
  acceptRun({ runId, dryRun = false, expectedReplacedRows, keepOffSchema }) {
    const expected =
      expectedReplacedRows == null ? "" : `&expectedReplacedRows=${expectedReplacedRows}`;
    const offSchema = keepOffSchema == null ? "" : `&keepOffSchema=${keepOffSchema}`;
    return axios
      .post(`${API_PATH}/runs/${runId}/accept?dryRun=${dryRun}${expected}${offSchema}`)
      .then(({ data }) => data);
  },

  /**
   * Writes a curated set, or with `dryRun` reports what such a write would do.
   *
   * `request` is the object the data-sorting service assembled — passed through unchanged rather than
   * rebuilt here, so the rows saved are the rows it read atomically from the reviewed frame.
   *
   * @param {{dataYearId: number, request: object, dryRun?: boolean}} params
   */
  saveCurated({ dataYearId, request, dryRun = false }) {
    return axios
      .post(`${API_PATH}/data-years/${dataYearId}/curated?dryRun=${dryRun}`, request)
      .then(({ data }) => data);
  },

  /** @param {{dataYearId: number, sourceFileInfoId: number}} params */
  createRun({ dataYearId, sourceFileInfoId }) {
    return axios
      .post(`${API_PATH}/data-years/${dataYearId}/runs`, { sourceFileInfoId })
      .then(({ data }) => data);
  },



  /**
   * Record which AI job this run is being served by.
   *
   * The agent reports this itself once it starts, but the TaskRunner polls on an interval, so between
   * enqueue and pickup the run row has no agentJobId — and without one there is nothing to poll or cancel.
   * The browser knows the id from the enqueue response, so it writes it straight away and a page reload in
   * that window still finds it.
   *
   * @param {{runId: (number|string), agentJobId: string, agentService: string}} params
   */
  reportRunJob({ runId, agentJobId, agentService }) {
    return axios
      .put(`${API_PATH}/runs/${runId}/progress`, { agentJobId, agentService })
      .then(({ data }) => data);
  },

  /**
   * Close a run from the browser: CANCELLED when the user abandons it, FAILED when the job could not be
   * enqueued at all.
   *
   * Reuses the agent's own terminal callback, which is safe because rows are only ever written by the
   * single terminal call — so closing a run that never produced any cannot destroy data. Without this a
   * run whose enqueue failed stayed QUEUED forever, with no task to cancel and no way to clear it.
   *
   * @param {{runId: (number|string), status: string, error: (string|undefined)}} params
   */
  /**
   * Removes a document from a workstream's sources, with its runs and extracted rows (ESG-4022). Promoted
   * figures stay. Server-side because the page lists sources as `files` plus every file with a run, so taking a
   * file off `files` alone left its run to bring it back on the next load.
   *
   * @param {{dataYearId: number, fileInfoId: (number|string)}} params
   */
  removeSource({ dataYearId, fileInfoId }) {
    return axios
      .delete(`${API_PATH}/data-years/${dataYearId}/sources/${encodeURIComponent(fileInfoId)}`)
      .then(({ data }) => data);
  },

  closeRun({ runId, status, error }) {
    return axios
      .put(`${API_PATH}/runs/${runId}/result`, { status, error: error ?? null, rows: [] })
      .then(({ data }) => data);
  },

};

export default api;
